<template>
  <div id="app" class="page-container tap-highlight-none">
    <router-view v-slot="{ Component, route }">
      <keep-alive>
        <component v-if="route.meta && route.meta.keepAlive" :is="Component" :key="route.name" />
      </keep-alive>
      <component
        v-if="!route.meta || !route.meta.keepAlive"
        :is="Component"
        :key="route.fullPath"
      />
    </router-view>
    <!-- SSE连接状态（开发环境显示） -->
    <!-- <SSEStatus v-if="!isProd" /> -->
    <!-- 性能监控组件（开发环境显示） -->
    <!-- <PerformanceMonitor v-if="!isProd" /> -->
    <!-- Store充值弹窗 -->
    <StoreDialog />
    <!-- 体力不足弹窗 -->
    <InsufficientPhysicalStrengthDialog />
    <!-- 底部标签栏 -->
    <TabBar v-if="shouldShowTabBar" />
    <VersionUpdateDialog
      v-model:show="showUpdateDialog"
      :nickname="userInfo?.nickname"
      :userId="userInfo?.userId"
      :email="userInfo?.email"
      :balance="userInfo?.balance"
      :update-content="['Client experience optimization', 'Fix known BUG issues']"
      @update="handleUpdate"
    />
    <!-- 全局顶部通知 -->
    <TopNotification />
    <AppNewUser />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, computed, ref } from 'vue'
import SSEStatus from './components/SSEStatus.vue'
import { VersionUpdateDialog, TopNotification } from '@/components/feedback'
import StoreDialog from './components/StoreDialog.vue'
import InsufficientPhysicalStrengthDialog from './components/InsufficientPhysicalStrengthDialog.vue'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/app'
import { useChatStore } from '@/stores/chat'
import { useRoute } from 'vue-router'
import { TabBar } from '@/components'
import { useUserStore } from '@/stores/user'
import { useStorePageStore } from '@/stores/store'
import successIcon from '@/assets/images/store/1.webp'
import errorIcon from '@/assets/images/store/2.webp'
import AppNewUser from './components/feedback/AppNewUser.vue'
import { useVipStore } from '@/stores/vip'
import { logEventSafe } from '@/plugins/firebase'
import { useRouter } from 'vue-router'
import * as NaturalCapacity from '@/utils/naturalCapacity'
import { useRoleWatcher, roleWatcherManager } from '@/composables/useRoleWatcher'
import { detailDB } from '@/utils/detailDB'
import { sceneCacheDB } from '@/utils/sceneCacheDB'
// import { chatRecordsDB } from '@/utils/chatRecordsDB'
// import { initPerformanceMonitor } from '@/utils/performanceMonitor'

const { locale } = useI18n()
const appStore = useAppStore()
const route = useRoute()
const router = useRouter()
const showUpdateDialog = computed(() => {
  if(route.name === 'Login') {
    return false
  }
  return userStore.getIsVersionUpdate()
})
const vipStore = useVipStore()
const chatStore = useChatStore()
const userInfo = ref({
  nickname: '',
  userId: '',
  email: '',
  balance: 0
})
// 判断是否为生产环境（非生产环境都显示 SSEStatus）
const isProd = import.meta.env.PROD

const userStore = useUserStore()
const storePageStore = useStorePageStore()
// 判断是否显示底部标签栏
const shouldShowTabBar = computed(() => {
  // 如果路由 meta 中设置了 hideTabbar 为 true，则不显示 TabBar
  if (route.meta?.hideTabbar) {
    return false
  }

  // 其他情况显示 TabBar
  return true
})

// 🔧 定义事件处理器（需要在 onMounted 之前定义，以便在组件挂载时注册）
const onlineHandler = () => NaturalCapacity.handleOnline({ appStore, chatStore })
const offlineHandler = () => NaturalCapacity.handleOffline({ appStore, chatStore })
const visibilityHandler = () => NaturalCapacity.handleVisibilityChange({ appStore })
const preloadHandler = (event: Event) =>
  NaturalCapacity.handlePreload(event, { userStore, vipStore, storePageStore }, router)

// 🔧 全局监听 role 变化
const stopRoleWatcher = useRoleWatcher(async (newRole, oldRole) => {
  // 触发全局 role 变化事件
  roleWatcherManager.emitRoleChange(newRole, oldRole)

  // 可以在这里添加全局的 role 变化处理逻辑
  console.log('🔄 Role changed:', { oldRole, newRole })

  // 例如：role 变化时刷新相关数据
  if (newRole !== oldRole) {
    // 执行一些全局操作
    if (userStore.getStoredToken()) {
      await userStore.setLoginTouristFunc()
    }
  }
}, true) // immediate: true 表示立即执行一次

// 初始化应用
onMounted(async () => {
  // 强制设置为英文
  locale.value = 'en-US'
  localStorage.setItem('locale', 'en-US')

  // 初始化应用状态
  appStore.init()

  // 🔍 初始化性能监控器
  // initPerformanceMonitor()

  // 监听网络状态
  window.addEventListener('online', onlineHandler)
  window.addEventListener('offline', offlineHandler)

  // 🔧 监听页面可见性变化（后台/前台切换）
  document.addEventListener('visibilitychange', visibilityHandler)

  // 🔧 监听后台预加载事件
  window.addEventListener('app:preload', preloadHandler)
  if (userStore.getStoredToken()) {
    userStore.setToken(localStorage.getItem('ai-chat-token') || '')
    userStore.setudid(localStorage.getItem('ai-chat-udid'))
    userStore.setAppId(localStorage.getItem('ai-chat-app-id'))
    await userStore.getUserInfo()
    
    // 初始化 SSE 连接（用户已登录）
    chatStore.initializeSSE()
  } else {
    // 游客模式也初始化 SSE
    chatStore.initializeSSE()
  }

  // 从新记录本地聊天记录
  if (!localStorage.getItem('is_chat_records_7')) {
    await chatStore.deleteAllDigitalHumanMessages()
    await detailDB.deleteAll()
    await sceneCacheDB.deleteAll()
    localStorage.setItem('is_chat_records_7', 'true')
    localStorage.removeItem('is_chat_records_6')
    localStorage.removeItem('is_chat_records_5')
    localStorage.removeItem('is_chat_records_4')
  }

  // 定期清理聊天记录（每7天，每个角色只保留最新30条）
  // await chatRecordsDB.cleanupChatRecords()
})

// 🔧 支付结果处理
window.addEventListener('paymentResult', (event: any) => {
  NaturalCapacity.handlePaymentResult(
    event,
    { userStore, vipStore, storePageStore },
    { successIcon, errorIcon },
    logEventSafe
  )
})

onUnmounted(() => {
  window.removeEventListener('online', onlineHandler)
  window.removeEventListener('offline', offlineHandler)
  document.removeEventListener('visibilitychange', visibilityHandler)
  document.removeEventListener('touchmove', handleTouchMove)
  window.removeEventListener('app:preload', preloadHandler)
  // 清理document事件
  document.removeEventListener('visibilitychange', visibilityHandler)
  document.removeEventListener('touchmove', handleTouchMove)

  // 🔧 清理 role 监听器
  stopRoleWatcher()
  roleWatcherManager.clear()
})

// 阻止下拉刷新
const handleTouchMove = (e: TouchEvent) => {
  if (e.touches.length > 1) return

  const target = e.target as HTMLElement
  const scrollTop = document.documentElement.scrollTop || document.body.scrollTop

  if (scrollTop === 0 && !target.closest('.allow-scroll')) {
    e.preventDefault()
  }
}

const handleUpdate = () => {
  userStore.setIsVersionUpdate(false)
}
</script>

<style lang="scss" scoped>
// 全局样式重置
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  // --safe-bottom: env(safe-area-inset-bottom);
  padding-bottom: var(--safe-bottom);
}

#app {
  height: 100%;
  min-height: 100%;
  /* 关键点：不要直接写 100vh */
  min-height: -webkit-fill-available;
  /* iOS Safari 可用高度 */
  overflow: visible;
  // 防止滚动链到整个页面，限定滚动在首页容器内
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y; // 仅允许纵向滚动，避免横向手势干扰
}

// 页面过渡动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.3s ease;
}

.slide-left-enter-from {
  transform: translateX(100%);
}

.slide-left-leave-to {
  transform: translateX(-100%);
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}

.slide-right-enter-from {
  transform: translateX(-100%);
}

.slide-right-leave-to {
  transform: translateX(100%);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease;
}

.slide-up-enter-from {
  transform: translateY(100%);
}

.slide-up-leave-to {
  transform: translateY(-100%);
}
</style>